import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, Cpu, Calendar, Star, Mail, LogOut, ExternalLink, Settings, Image } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/team", label: "Team", icon: Users },
  { to: "/admin/robots", label: "Robots", icon: Cpu },
  { to: "/admin/outreach", label: "Outreach", icon: Calendar },
  { to: "/admin/media", label: "Gallery", icon: Image },
  { to: "/admin/sponsors", label: "Sponsors", icon: Star },
  { to: "/admin/messages", label: "Messages", icon: Mail },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen bg-voltage-bg flex items-center justify-center text-voltage-muted font-mono">
        Authenticating...
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const onLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-voltage-bg flex">
      <aside className="w-64 border-r border-voltage-blue/30 bg-voltage-bg flex flex-col" data-testid="admin-sidebar">
        <div className="p-6 border-b border-voltage-blue/30">
          <div className="font-display font-black text-xl tracking-wider">
            <span className="text-white">VOLTAGE</span>{" "}
            <span className="text-voltage-yellow">CTRL</span>
          </div>
          <div className="text-xs text-voltage-muted font-mono mt-1">{user.email}</div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 font-display font-bold text-xs uppercase tracking-widest transition-all ${
                  isActive
                    ? "bg-voltage-blue text-white border-l-4 border-voltage-yellow"
                    : "text-voltage-muted hover:text-white hover:bg-voltage-blue/10"
                }`
              }
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-voltage-blue/30 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            data-testid="admin-view-site"
            className="flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest text-voltage-muted hover:text-voltage-yellow"
          >
            <ExternalLink className="w-3.5 h-3.5" /> View Site
          </a>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest text-voltage-muted hover:text-red-400"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 p-8 md:p-12 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
